// Port of webapp/src/components/gallery/gallery.tsx + galleryCard.tsx

import {ReactElement} from 'react'
import classnames from 'classnames'
import {Board, BoardGroup, IPropertyTemplate} from '../../types/board'
import {BoardView} from '../../types/boardView'
import {IUser} from '../../types/user'
import {Block} from '../../types/block'
import * as mutator from '../../state/mutator'
import {Icon} from '../common/icons'
import PropertyValue from '../propertyValue'
import {optionColorCssVar} from '../../state/properties'
import {cardPreview} from '../cardBadges'

type Props = {
    board: Board
    view: BoardView
    groups: BoardGroup[]
    usersById: Record<string, IUser>
    blocksById: Record<string, Block>
    readOnly: boolean
    onCardClicked: (cardId: string) => void
}

export default function Gallery({board, view, groups, usersById, blocksById, readOnly, onCardClicked}: Props): ReactElement {
    const visibleProps: IPropertyTemplate[] = view.fields.visiblePropertyIds.length ?
        board.cardProperties.filter((p) => view.fields.visiblePropertyIds.includes(p.id)) :
        board.cardProperties

    const groupByProperty = view.fields.groupById ? board.cardProperties.find((p) => p.id === view.fields.groupById) : undefined

    const addCard = async (optionId: string): Promise<void> => {
        const properties: Record<string, string> = {}
        if (groupByProperty && optionId) {
            properties[groupByProperty.id] = optionId
        }
        const card = await mutator.createCard(board, view, {properties})
        onCardClicked(card.id)
    }

    return (
        <div className='Gallery flex-1 overflow-auto pb-10'>
            {groups.map((group) => (
                <div key={group.option.id || 'blank'} className='mb-7'>
                    {groupByProperty && (
                        <div className='mb-2 flex items-center gap-2 font-semibold'>
                            <span
                                className='h-3 w-3 rounded-full'
                                style={{backgroundColor: optionColorCssVar(group.option.color)}}
                                aria-hidden={true}
                            />
                            <span>{group.option.value || `未设置${groupByProperty.name}`}</span>
                            <span className='opacity-50'>{group.cards.length}</span>
                        </div>
                    )}

                    <div className='flex flex-wrap gap-4'>
                        {group.cards.map((card) => {
                            const preview = cardPreview(card, blocksById)
                            return (
                                <div
                                    key={card.id}
                                    className={classnames('BoardCard w-[280px] cursor-pointer p-0')}
                                    onClick={() => onCardClicked(card.id)}
                                    role='button'
                                    tabIndex={0}
                                    onKeyDown={(event) => event.key === 'Enter' && onCardClicked(card.id)}
                                    aria-label={card.title || '未命名'}
                                >
                                    <div
                                        className='h-[120px] rounded-t'
                                        style={{background: preview.hasImage ? 'rgba(63,67,80,.10)' : 'linear-gradient(135deg, rgba(63,67,80,.07), rgba(63,67,80,.14))'}}
                                    >
                                        {preview.hasImage ? (
                                            <div className='flex h-full items-center justify-center opacity-40'>
                                                <Icon name='image' size={28}/>
                                            </div>
                                        ) : (
                                            <div className='flex h-full items-center justify-center text-3xl opacity-30' aria-hidden={true}>
                                                {card.fields.icon || '📄'}
                                            </div>
                                        )}
                                    </div>

                                    <div className='p-4'>
                                        <div className='mb-2 flex items-start gap-2 font-semibold'>
                                            {card.fields.icon && <span aria-hidden={true}>{card.fields.icon}</span>}
                                            <span className={classnames('truncate', !card.title && 'opacity-40')}>{card.title || '未命名'}</span>
                                        </div>
                                        {preview.text && <div className='mb-2 line-clamp-2 text-xs opacity-70'>{preview.text}</div>}
                                        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                                            {visibleProps.map((template) => (
                                                <PropertyValue
                                                    key={template.id}
                                                    template={template}
                                                    card={card}
                                                    usersById={usersById}
                                                    />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}

                        {!readOnly && (
                            <button
                                type='button'
                                onClick={() => addCard(group.option.id)}
                                className='BoardCard flex h-[196px] w-[280px] items-center justify-center gap-2 text-sm opacity-70 hover:opacity-100'
                            >
                                <Icon name='add' size={16}/>
                                新建
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    )
}
